import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Phone, Mail, MapPin, ArrowRight, ImagePlus, X } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { api, formatApiError } from "@/lib/api";
import { trackCall, trackQuoteSubmit } from "@/lib/analytics";
import { CONTACT, SERVICE_OPTIONS, BUDGET_OPTIONS } from "@/data/content";

const EMPTY = { name: "", email: "", phone: "", service: SERVICE_OPTIONS[0], suburb: "", budget: "", message: "" };
const MAX_PHOTOS = 8;
const MAX_BYTES = 10 * 1024 * 1024;

export default function Enquiry() {
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((f) => f.type.startsWith("image/") && f.size <= MAX_BYTES);
    if (valid.length < files.length) toast.error("Some files were skipped (images under 10MB only).");
    setPhotos((prev) => [...prev, ...valid].slice(0, MAX_PHOTOS));
    e.target.value = "";
  };

  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      photos.forEach((p) => fd.append("photos", p));
      await api.post("/enquiries", fd);
      trackQuoteSubmit("enquiry_section");
      setForm(EMPTY);
      setPhotos([]);
      navigate("/thank-you");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Please call us.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#0f172a]/20 focus:border-[#9c7a4d] text-[#0f172a] placeholder:text-[#94a3b8] py-3 text-lg font-light outline-none transition-colors duration-300";

  return (
    <section id="enquire" data-testid="enquiry-section" className="relative bg-[#f8fafc] py-24 md:py-40">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <p className="overline !text-[#9c7a4d] mb-6">Get a Quote</p>
          <h2 className="font-serif font-light text-[#0f172a] text-4xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight mb-10">
            <Reveal>Let's build</Reveal>
            <Reveal delay={0.08}>something</Reveal>
            <Reveal delay={0.16}>exceptional.</Reveal>
          </h2>
          <p className="text-[#475569] text-lg font-light leading-relaxed max-w-md mb-12">
            Tell us about your project and we'll get back to you with honest advice and a considered quote.
          </p>

          <div className="space-y-6">
            <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCall("enquiry_section")} data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="h-11 w-11 flex items-center justify-center border border-[#0f172a]/15 group-hover:border-[#9c7a4d] transition-colors">
                <Phone className="h-4 w-4 text-[#9c7a4d]" />
              </span>
              <div>
                <p className="overline !text-[#94a3b8] mb-0.5">Call</p>
                <p className="text-[#0f172a] group-hover:text-[#9c7a4d] transition-colors">{CONTACT.phone}</p>
              </div>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="h-11 w-11 flex items-center justify-center border border-[#0f172a]/15 group-hover:border-[#9c7a4d] transition-colors">
                <Mail className="h-4 w-4 text-[#9c7a4d]" />
              </span>
              <div>
                <p className="overline !text-[#94a3b8] mb-0.5">Email</p>
                <p className="text-[#0f172a] group-hover:text-[#9c7a4d] transition-colors">{CONTACT.email}</p>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="h-11 w-11 flex items-center justify-center border border-[#0f172a]/15">
                <MapPin className="h-4 w-4 text-[#9c7a4d]" />
              </span>
              <div>
                <p className="overline !text-[#94a3b8] mb-0.5">Servicing</p>
                <p className="text-[#0f172a]">{CONTACT.area}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} data-testid="enquiry-form" className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div className="sm:col-span-1">
                <label className="overline !text-[#94a3b8] block mb-3">Name</label>
                <input data-testid="enquiry-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputClass} />
              </div>
              <div className="sm:col-span-1">
                <label className="overline !text-[#94a3b8] block mb-3">Phone</label>
                <input data-testid="enquiry-phone" required value={form.phone} onChange={set("phone")} placeholder="04xx xxx xxx" className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label className="overline !text-[#94a3b8] block mb-3">Email</label>
                <input data-testid="enquiry-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label className="overline !text-[#94a3b8] block mb-3">Service</label>
                <select data-testid="enquiry-service" value={form.service} onChange={set("service")} className={`${inputClass} appearance-none cursor-pointer`}>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s} className="bg-white text-[#0f172a]">{s}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-1">
                <label className="overline !text-[#94a3b8] block mb-3">Suburb <span className="normal-case tracking-normal text-[#94a3b8]">(optional)</span></label>
                <input data-testid="enquiry-suburb" value={form.suburb} onChange={set("suburb")} placeholder="e.g. Cottesloe" className={inputClass} />
              </div>
              <div className="sm:col-span-1">
                <label className="overline !text-[#94a3b8] block mb-3">Budget <span className="normal-case tracking-normal text-[#94a3b8]">(optional)</span></label>
                <select data-testid="enquiry-budget" value={form.budget} onChange={set("budget")} className={`${inputClass} appearance-none cursor-pointer`}>
                  <option value="" className="bg-white text-[#0f172a]">Select a range…</option>
                  {BUDGET_OPTIONS.map((b) => (
                    <option key={b} value={b} className="bg-white text-[#0f172a]">{b}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="overline !text-[#94a3b8] block mb-3">Project Details</label>
                <textarea data-testid="enquiry-message" required rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about the space, materials and timeline…" className={`${inputClass} resize-none`} />
              </div>
              <div className="sm:col-span-2">
                <label className="overline !text-[#94a3b8] block mb-3">Photos (optional)</label>
                <label
                  data-testid="enquiry-photos-input"
                  className="flex items-center justify-center gap-3 border border-dashed border-[#0f172a]/25 hover:border-[#9c7a4d] text-[#64748b] hover:text-[#9c7a4d] py-6 cursor-pointer transition-colors"
                >
                  <ImagePlus className="h-5 w-5" />
                  <span className="text-sm">Add photos of what you'd like done</span>
                  <input type="file" accept="image/*" multiple onChange={addPhotos} className="hidden" />
                </label>
                {photos.length > 0 && (
                  <div data-testid="enquiry-photo-previews" className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {photos.map((p, i) => (
                      <div key={i} className="relative group aspect-square border border-[#0f172a]/10 overflow-hidden">
                        <img src={URL.createObjectURL(p)} alt={p.name} className="h-full w-full object-cover" />
                        <button
                          type="button"
                          data-testid={`enquiry-photo-remove-${i}`}
                          onClick={() => removePhoto(i)}
                          className="absolute top-1 right-1 h-6 w-6 flex items-center justify-center bg-black/60 text-white hover:bg-black/80 transition-colors"
                          aria-label="Remove photo"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-[#94a3b8] mt-2">Up to {MAX_PHOTOS} images, 10MB each.</p>
              </div>
              <div className="sm:col-span-2 mt-2">
                <button
                  data-testid="enquiry-submit"
                  type="submit"
                  disabled={loading}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#9c7a4d] text-white hover:bg-[#0f172a] disabled:opacity-60 px-10 py-4 text-xs uppercase tracking-[0.22em] transition-colors duration-300"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
                  {loading ? "Sending" : "Send Enquiry"}
                </button>
              </div>
            </form>
        </div>
      </div>
    </section>
  );
}
